/* eslint-disable @typescript-eslint/no-explicit-any */
import Screen from "@/src/components/Screen";
import { Stepper } from "@mantine/core";
import { useEffect, useState } from "react";
import Step1 from "./Form/Step1";
import { textVariants } from "@/src/components/Text/Text";
import apiRoutes from "@/src/apiRoutes";
import api from "@/src/utils";
import { P2PComponentsData, SingleOffer } from "../../P2PTrade/types";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { useForm } from "react-hook-form";
import Step2 from "./Form/Step2";
import Step3 from "./Form/Step3";
import { zodResolver } from "@hookform/resolvers/zod";
import { useParams } from "react-router-dom";
import { isEmpty } from "lodash";
import { useTranslation } from "react-i18next";

const createState = z.object({
  amount: z.number(),
  price_type: z.string(),
  coin: z.number(),
  rate: z.string().nonempty({ message: "This field is required" }),
  trans_type: z.string(),
  currency: z.string(),
  payment_methods: z.number().array(),
  terms: z.string(),
  max_order_price: z.string().nonempty({ message: "This field is required" }),
  min_order_price: z.string().nonempty({ message: "This field is required" }),
  offer_time_limit: z.number(),
});

export type createType = z.infer<typeof createState>;

const Update = () => {
  const { t } = useTranslation();
  const trans = (key: string) => t(`app.p2p.edit.${key}`);
  const { id } = useParams();

  const validSteps = [
    {
      id: "1",
      fields: [
        "min_order_price",
        "max_order_price",
        "price_type",
        "coin",
        "currency",
        "rate",
        "amount",
      ],
    },
    {
      id: "2",
      fields: ["terms", "payment_methods"],
    },
  ];

  const [active, setActive] = useState(0);

  const getOffer = async () => {
    const res = await api.post(apiRoutes.offers, {
      uuid: id,
      type: "detail",
    });
    return res.data;
  };

  const { data: singleOffer } = useQuery<SingleOffer>({
    queryKey: ["offer", id],
    queryFn: getOffer,
  });

  useEffect(() => {
    if (singleOffer?.info) {
      createForm.setValue("min_order_price", singleOffer?.info.bounds.fiat.min);
      createForm.setValue("max_order_price", singleOffer?.info.bounds.fiat.max);
      createForm.setValue("price_type", singleOffer?.info.price_type);
      createForm.setValue("coin", singleOffer?.info.coin__id);
      createForm.setValue("rate", singleOffer?.info.unit_price);
      createForm.setValue("amount", Number(singleOffer?.info.trading_amount));
      // createForm.setValue(
      //   "payment_methods",
      //   singleOffer?.info.payment_method.map((item) => item.id)
      // );
      createForm.setValue("terms", singleOffer?.info.terms);
      createForm.setValue(
        "offer_time_limit",
        singleOffer?.info.offer_time_limit
      );
      createForm.setValue("trans_type", singleOffer?.info.offer_type);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleOffer]);

  const fetchP2pComponents = async () => {
    const res = await api.post(
      createForm.watch("currency")
        ? `${apiRoutes.fetchP2pComponents}${createForm
            .watch("currency")
            .slice(0, 3)}/`
        : apiRoutes.fetchP2pComponents,
      createForm.watch("coin") && {
        coin: createForm.watch("coin"),
      }
    );
    return res.data;
  };
  const createForm = useForm<createType>({
    defaultValues: {
      trans_type: "buy",
      offer_time_limit: 30,
    },
    resolver: zodResolver(createState),
  });

  const { data } = useQuery<P2PComponentsData>({
    queryKey: [
      "fetch_p2p_components",
      createForm.watch("currency"),
      createForm.watch("coin"),
    ],
    queryFn: fetchP2pComponents,
  });

  const next = async () => {
    const fields = validSteps[active].fields;
    const output = await createForm.trigger(fields as any, {
      shouldFocus: true,
    });
    if (!output) return;
    setActive((prev) => prev + 1);
  };

  const back = () => {
    setActive((prev) => prev - 1);
  };

  // Effect to set currency
  useEffect(() => {
    if (data?.info.currencies && singleOffer) {
      const foundCurrency = data?.info.currencies.find(
        (item) => item.currency === singleOffer?.info.currency
      );
      if (isEmpty(createForm.watch("currency"))) {
        createForm.setValue(
          "currency",
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          foundCurrency!.currency + foundCurrency?.id.toString()
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.info.currencies, singleOffer]);

  return (
    <Screen>
      <div className="md:pb-4">
        <span className={textVariants({ size: "xxl", font: "semibold" })}>
          {trans("header")}
        </span>
      </div>
      <div className="md:px-10 px-2 py-5">
        <Stepper
          size="xs"
          color="#2F60E0"
          active={active}
          onStepClick={setActive}
          allowNextStepsSelect={false}
        >
          <Stepper.Step>
            <Step1 next={next} data={data} form={createForm} />
          </Stepper.Step>
          <Stepper.Step>
            <Step2
              back={back}
              data={data}
              form={createForm}
              setActive={setActive}
            />
          </Stepper.Step>
          <Stepper.Step>
            <Step3 />
          </Stepper.Step>
        </Stepper>
      </div>
    </Screen>
  );
};

export default Update;
